import React from "react";

import { isSipNotReadyError } from "~/services/calling/sipErrors";

export type IncomingCallToastPayload = {
  id: string;
  customerName?: string | null;
  callerNumber?: string | null;
  displayNumber?: string;
  service?: string | null;
  matchType?: "customer" | "emergency-contact";
};

type CallNotificationToastProps = {
  incomingCall: IncomingCallToastPayload | null;
  stackOffset?: number;
  onAnswer: (incomingId: string) => Promise<void>;
  onDecline: (incomingId: string) => Promise<void>;
  onHold: (incomingId: string) => Promise<void>;
  answerDisabled?: boolean;
  answerDisabledMessage?: string;
};

function cn(...classes: Array<string | undefined | false | null>) {
  return classes.filter(Boolean).join(" ");
}

export default function CallNotificationToast({
  incomingCall,
  stackOffset = 0,
  onAnswer,
  onDecline,
  onHold,
  answerDisabled = false,
  answerDisabledMessage,
}: CallNotificationToastProps) {
  const [loadingAction, setLoadingAction] = React.useState<
    "answer" | "decline" | "hold" | null
  >(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!incomingCall) {
      setLoadingAction(null);
      setActionError(null);
    }
  }, [incomingCall]);

  if (!incomingCall) return null;

  const customerLabel = incomingCall.customerName ?? "Known caller";
  const sourceLabel =
    incomingCall.matchType === "emergency-contact"
      ? "Emergency Contact"
      : "Registered Customer";
  const isAnswerDisabled = loadingAction !== null || answerDisabled;

  return (
    <div
      className="pointer-events-auto w-full"
      style={stackOffset > 0 ? { marginTop: 0 } : undefined}
    >
      <div
        className={cn(
          "rounded-[14px] border border-[#2C313B] bg-[#0F1622] p-4",
          "shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#4FA4E4]">
              Incoming Call
            </div>
            <div className="mt-1 text-[16px] font-semibold text-white">
              {customerLabel}
            </div>
            <div className="mt-1 text-[13px] text-[#99A1AA]">
              {incomingCall.displayNumber ??
                incomingCall.callerNumber ??
                "Unknown number"}
            </div>
            <div className="mt-2 inline-flex items-center rounded-full border border-[#2C313B] px-2.5 py-1 text-[11px] font-semibold text-[#99A1AA]">
              {sourceLabel}
            </div>
          </div>
          <div className="rounded-full border border-[#2C313B] px-2 py-1 text-[11px] text-[#99A1AA]">
            {incomingCall.service ?? "Voice Call"}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            disabled={loadingAction !== null}
            onClick={async () => {
              setLoadingAction("decline");
              try {
                await onDecline(incomingCall.id);
              } finally {
                setLoadingAction(null);
              }
            }}
            className={cn(
              "h-9 flex-1 rounded-[10px] border border-[#2C313B] bg-[#1A222D]",
              "text-[14px] font-semibold text-white",
              "hover:bg-[#202937] active:bg-[#161D27]",
              loadingAction !== null && "cursor-not-allowed opacity-60"
            )}
          >
            {loadingAction === "decline" ? "Declining..." : "Decline"}
          </button>
          <button
            type="button"
            disabled={loadingAction !== null}
            onClick={async () => {
              setLoadingAction("hold");
              try {
                await onHold(incomingCall.id);
              } finally {
                setLoadingAction(null);
              }
            }}
            className={cn(
              "h-9 flex-1 rounded-[10px] border border-[#66510B] bg-[#2A2109]",
              "text-[14px] font-semibold text-[#F1C453]",
              "hover:brightness-110 active:brightness-95",
              loadingAction !== null && "cursor-not-allowed opacity-60"
            )}
          >
            {loadingAction === "hold" ? "Holding..." : "Hold"}
          </button>
          <button
            type="button"
            disabled={isAnswerDisabled}
            title={answerDisabled ? answerDisabledMessage : undefined}
            onClick={async () => {
              if (answerDisabled) return;
              setLoadingAction("answer");
              setActionError(null);
              try {
                await onAnswer(incomingCall.id);
              } catch (error) {
                if (isSipNotReadyError(error)) {
                  setActionError(error.message);
                  return;
                }
                throw error;
              } finally {
                setLoadingAction(null);
              }
            }}
            className={cn(
              "h-9 flex-1 rounded-[10px] border border-[#0A6D3A] bg-[#0F7C43]",
              "text-[14px] font-semibold text-white",
              "hover:brightness-110 active:brightness-95",
              isAnswerDisabled && "cursor-not-allowed opacity-60"
            )}
          >
            {loadingAction === "answer" ? "Answering..." : "Answer"}
          </button>
        </div>
        {answerDisabled && answerDisabledMessage ? (
          <p className="mt-2 text-[12px] leading-4 text-[#F1C453]">
            {answerDisabledMessage}
          </p>
        ) : null}
        {actionError ? (
          <p className="mt-2 text-[12px] leading-4 text-rose-300">{actionError}</p>
        ) : null}
      </div>
    </div>
  );
}
